import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Grid,
  Column,
  Tile,
  Search,
  Modal,
  TextInput,
  InlineLoading,
  InlineNotification,
} from '@carbon/react';
import {
  Add,
  Edit,
  TrashCan,
  Time,
  List,
  Grid as GridIcon,
} from '@carbon/icons-react';
import PipelineIcon from '../components/icons/PipelineIcon';
import { useTheme } from '../context/ThemeContext';
import { usePipelines } from '../hooks/usePipelines';
import { formatRelativeTime } from '../lib/timeUtils';

const PipelinesListPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { pipelines, isLoading, error, create, remove, refetch } = usePipelines();

  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('cards');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newPipelineName, setNewPipelineName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [pipelineToDelete, setPipelineToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  const filteredPipelines = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return pipelines.filter(
      (p) =>
        (p.name || '').toLowerCase().includes(term) ||
        (p.id || '').toLowerCase().includes(term)
    );
  }, [pipelines, searchTerm]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const openCreateModal = () => {
    setNewPipelineName('');
    setCreateError(null);
    setCreateModalOpen(true);
  };

  const handleCreateConfirm = async () => {
    const name = newPipelineName.trim();
    if (!name) {
      setCreateError('Please enter a pipeline name');
      return;
    }

    setIsCreating(true);
    setCreateError(null);
    try {
      const pipeline = await create(name);
      setCreateModalOpen(false);
      navigate(`/dashboard/pipelines/${pipeline.id}`);
    } catch (err) {
      setCreateError(err.message || 'Failed to create pipeline');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteClick = (pipeline) => {
    setPipelineToDelete(pipeline);
    setDeleteError(null);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!pipelineToDelete) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await remove(pipelineToDelete.id);
      setDeleteModalOpen(false);
      setPipelineToDelete(null);
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete pipeline');
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div
        style={{
          backgroundColor: bgPrimary,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        data-testid="pipelines-list-page"
      >
        <InlineLoading description="Loading pipelines..." />
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: bgPrimary, minHeight: '100vh' }} data-testid="pipelines-list-page">
      <div
        style={{
          backgroundColor: bgSecondary,
          borderBottom: `1px solid ${borderColor}`,
          padding: '16px 0',
        }}
      >
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '0 16px' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '12px',
                }}
              >
                <div>
                  <h1
                    style={{
                      color: textPrimary,
                      fontSize: 'clamp(1.25rem, 4vw, 1.75rem)',
                      fontWeight: 600,
                      marginBottom: '4px',
                    }}
                  >
                    Pipelines
                  </h1>
                  <p style={{ color: textSecondary, fontSize: '0.875rem' }}>
                    {filteredPipelines.length} of {pipelines.length} pipelines
                  </p>
                </div>
                <Button
                  kind="primary"
                  renderIcon={Add}
                  onClick={openCreateModal}
                  data-testid="new-pipeline-btn"
                  size="md"
                >
                  New Pipeline
                </Button>
              </div>

              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 200px', minWidth: '200px', maxWidth: '100%' }}>
                  <Search
                    labelText="Search"
                    placeholder="Search pipelines..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="md"
                    data-testid="search-pipelines"
                  />
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Button
                    kind={viewMode === 'cards' ? 'secondary' : 'ghost'}
                    size="sm"
                    hasIconOnly
                    renderIcon={GridIcon}
                    iconDescription="Card view"
                    onClick={() => setViewMode('cards')}
                  />
                  <Button
                    kind={viewMode === 'table' ? 'secondary' : 'ghost'}
                    size="sm"
                    hasIconOnly
                    renderIcon={List}
                    iconDescription="Table view"
                    onClick={() => setViewMode('table')}
                  />
                </div>
              </div>
            </div>
          </Column>
        </Grid>
      </div>

      {error && (
        <div style={{ padding: '16px' }}>
          <InlineNotification
            kind="error"
            title="Error loading pipelines"
            subtitle={error}
            lowContrast
            actions={
              <Button kind="ghost" size="sm" onClick={refetch}>
                Retry
              </Button>
            }
          />
        </div>
      )}

      <div style={{ padding: '16px 0' }}>
        <Grid>
          {filteredPipelines.length === 0 ? (
            <Column lg={16} md={8} sm={4}>
              <Tile
                style={{
                  backgroundColor: bgSecondary,
                  border: 'none',
                  padding: '32px 16px',
                  textAlign: 'center',
                }}
              >
                <PipelineIcon size={48} style={{ color: textSecondary, marginBottom: '16px' }} />
                <h3
                  style={{
                    color: textPrimary,
                    marginBottom: '8px',
                    fontSize: 'clamp(1rem, 4vw, 1.25rem)',
                  }}
                >
                  No Pipelines
                </h3>
                <p style={{ color: textSecondary, marginBottom: '24px', fontSize: '0.875rem' }}>
                  Create your first ETL pipeline to transform and route sensor data
                </p>
                <Button kind="primary" renderIcon={Add} onClick={openCreateModal} size="md">
                  Create Pipeline
                </Button>
              </Tile>
            </Column>
          ) : viewMode === 'cards' ? (
            filteredPipelines.map((pipeline) => (
              <Column lg={8} md={8} sm={4} key={pipeline.id}>
                <Tile
                  style={{
                    backgroundColor: bgSecondary,
                    border: 'none',
                    padding: '16px',
                    marginBottom: '16px',
                  }}
                  data-testid={`pipeline-card-${pipeline.id}`}
                >
                  <div style={{ marginBottom: '12px' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: '12px',
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h3
                          style={{
                            color: textPrimary,
                            fontSize: '1.125rem',
                            fontWeight: 600,
                            marginBottom: '4px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {pipeline.name || 'Untitled Pipeline'}
                        </h3>
                        <p style={{ color: textSecondary, fontSize: '0.75rem' }}>
                          {pipeline.node_count ?? 0} nodes
                        </p>
                      </div>
                      <PipelineIcon size={24} style={{ color: textSecondary, flexShrink: 0 }} />
                    </div>
                  </div>

                  <div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        color: textSecondary,
                        fontSize: '0.75rem',
                        marginBottom: '12px',
                      }}
                    >
                      <Time size={14} />
                      <span>
                        Updated{' '}
                        {pipeline.updated_at
                          ? formatRelativeTime(pipeline.updated_at)
                          : formatRelativeTime(pipeline.created_at)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <Button
                        kind="primary"
                        size="sm"
                        renderIcon={Edit}
                        onClick={() => navigate(`/dashboard/pipelines/${pipeline.id}`)}
                      >
                        Edit
                      </Button>
                      <Button
                        kind="danger--ghost"
                        size="sm"
                        renderIcon={TrashCan}
                        onClick={() => handleDeleteClick(pipeline)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </Tile>
              </Column>
            ))
          ) : (
            <Column lg={16} md={8} sm={4}>
              <div style={{ overflowX: 'auto', padding: '0 16px' }}>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    backgroundColor: bgSecondary,
                  }}
                >
                  <thead>
                    <tr>
                      {['Name', 'Nodes', 'Updated', 'Actions'].map((header) => (
                        <th
                          key={header}
                          style={{
                            padding: '12px 16px',
                            textAlign: 'left',
                            color: textSecondary,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                          }}
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPipelines.map((pipeline) => (
                      <tr
                        key={pipeline.id}
                        data-testid={`pipeline-row-${pipeline.id}`}
                      >
                        <td style={{ padding: '12px 16px', color: textPrimary }}>
                          {pipeline.name || 'Untitled Pipeline'}
                        </td>
                        <td style={{ padding: '12px 16px', color: textSecondary }}>
                          {pipeline.node_count ?? 0}
                        </td>
                        <td style={{ padding: '12px 16px', color: textSecondary, fontSize: '0.875rem' }}>
                          {formatDate(pipeline.updated_at || pipeline.created_at)}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <Button
                              kind="ghost"
                              size="sm"
                              renderIcon={Edit}
                              onClick={() => navigate(`/dashboard/pipelines/${pipeline.id}`)}
                            >
                              Edit
                            </Button>
                            <Button
                              kind="danger--ghost"
                              size="sm"
                              renderIcon={TrashCan}
                              onClick={() => handleDeleteClick(pipeline)}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Column>
          )}
        </Grid>
      </div>

      <Modal
        open={createModalOpen}
        modalHeading="New Pipeline"
        primaryButtonText={isCreating ? 'Creating...' : 'Create'}
        secondaryButtonText="Cancel"
        primaryButtonDisabled={isCreating}
        onRequestClose={() => !isCreating && setCreateModalOpen(false)}
        onRequestSubmit={handleCreateConfirm}
        data-testid="create-pipeline-modal"
      >
        <p style={{ marginBottom: '16px', color: textSecondary }}>
          Enter a name for your pipeline. You can build the flow in the editor after creating it.
        </p>
        <TextInput
          id="pipeline-name"
          labelText="Pipeline name"
          placeholder="e.g. Cold room MQTT to Supabase"
          value={newPipelineName}
          onChange={(e) => setNewPipelineName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !isCreating) handleCreateConfirm();
          }}
          invalid={!!createError}
          invalidText={createError}
          autoFocus
          data-testid="pipeline-name-input"
        />
      </Modal>

      <Modal
        open={deleteModalOpen}
        danger
        modalHeading="Delete Pipeline"
        primaryButtonText={isDeleting ? 'Deleting...' : 'Delete'}
        secondaryButtonText="Cancel"
        primaryButtonDisabled={isDeleting}
        onRequestClose={() => !isDeleting && setDeleteModalOpen(false)}
        onRequestSubmit={handleDeleteConfirm}
      >
        {deleteError && (
          <InlineNotification
            kind="error"
            title="Delete failed"
            subtitle={deleteError}
            lowContrast
            style={{ marginBottom: '16px' }}
          />
        )}
        <p>
          Are you sure you want to delete{' '}
          <strong>{pipelineToDelete?.name || 'this pipeline'}</strong>? This action cannot be
          undone.
        </p>
      </Modal>
    </div>
  );
};

export default PipelinesListPage;
